/**
 * RateViewDialog
 *
 * Combined rating dialog for views: lets the user submit or edit their own
 * rating and browse other team members' reviews in a paginated scrollable list.
 *
 * Reuses contentService methods that operate on content IDs. Views are content
 * rows (kind='view') post-Plan-A, so the same endpoints work without changes.
 */

import React, { useEffect, useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Rating,
  TextField,
  CircularProgress,
  Divider,
  Stack,
} from '@mui/material';
import { contentService } from '../../services';

interface OtherReview {
  id: string;
  userId: string;
  userName: string;
  rating: number;
  comment: string;
  createdAt: Date | null;
}

interface RateViewDialogProps {
  open: boolean;
  onClose: () => void;
  viewId: string;
  teamId: string;
  viewName: string;
  currentUserId: string;
  onSubmitted?: (averageRating?: number, totalRatings?: number) => void;
}

const PAGE_SIZE = 10;

export const RateViewDialog: React.FC<RateViewDialogProps> = ({
  open,
  onClose,
  viewId,
  teamId,
  viewName,
  currentUserId,
  onSubmitted,
}) => {
  // Submit-section state
  const [userRating, setUserRating] = useState<number>(0);
  const [userComment, setUserComment] = useState<string>('');
  const [isEditingReview, setIsEditingReview] = useState<boolean>(false);
  const [submitBusy, setSubmitBusy] = useState<boolean>(false);

  // Browse-section state
  const [others, setOthers] = useState<OtherReview[]>([]);
  const [othersPage, setOthersPage] = useState<number>(1);
  const [hasMore, setHasMore] = useState<boolean>(false);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [avgRating, setAvgRating] = useState<number>(0);
  const [othersLoading, setOthersLoading] = useState<boolean>(false);

  // Load initial data when the dialog opens
  useEffect(() => {
    if (!open || !viewId) return;

    const load = async () => {
      // Reset state on open
      setUserRating(0);
      setUserComment('');
      setIsEditingReview(false);
      setOthers([]);
      setOthersPage(1);
      setHasMore(false);
      setTotalCount(0);
      setAvgRating(0);

      // Load user's own rating
      try {
        const own = await contentService.getUserContentRating(viewId);
        if (own && own.rating) {
          setUserRating(own.rating);
          setUserComment(own.comment ?? '');
          setIsEditingReview(true);
        }
      } catch (err) {
        // Non-fatal — user has not rated, or backend unavailable
        console.warn('getUserContentRating failed:', err);
      }

      // Load first page of others' reviews
      await loadOthersPage(1, /* append= */ false);
    };

    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, viewId]);

  const loadOthersPage = useCallback(
    async (page: number, append: boolean) => {
      if (!viewId) return;
      setOthersLoading(true);
      try {
        const response = await contentService.getContentRatingsPaginated(viewId, {
          page,
          limit: PAGE_SIZE,
          sort_by: 'date',
          sort_order: 'desc',
        });
        const raw = response.data || response.ratings || [];
        const filtered = raw.filter((r: any) =>
          (r.userId || r.user_id) !== currentUserId
        );
        const transformed: OtherReview[] = filtered.map((r: any) => {
          const createdValue = r.createdAt || r.created_at;
          return {
            id: r.id,
            userId: r.userId || r.user_id,
            userName: r.userName || r.user_name || `User ${(r.userId || r.user_id || '').slice(-8)}`,
            rating: r.rating,
            comment: r.comment ?? '',
            createdAt: createdValue ? new Date(createdValue) : null,
          };
        });
        setOthers((prev) => (append ? [...prev, ...transformed] : transformed));

        const pagination = response.metadata?.pagination || response.pagination || {};
        setHasMore(Boolean(pagination.hasNextPage));
        setTotalCount(pagination.totalCount || 0);

        // Pull aggregate average from the first page response if present
        const ratingsSummary = (response as any).summary || (response as any).rating_summary;
        if (ratingsSummary?.average_rating !== undefined) {
          setAvgRating(ratingsSummary.average_rating);
        }
      } catch (err) {
        console.error('getContentRatingsPaginated failed:', err);
      } finally {
        setOthersLoading(false);
      }
    },
    [viewId, currentUserId],
  );

  const handleShowMore = () => {
    const next = othersPage + 1;
    setOthersPage(next);
    loadOthersPage(next, /* append= */ true);
  };

  const handleSubmit = async () => {
    if (userRating === 0 || submitBusy) return;
    setSubmitBusy(true);
    try {
      const result = await contentService.rateContentWithComment(
        viewId,
        userRating,
        teamId,
        userComment || undefined,
      );
      onSubmitted?.(result.averageRating, result.totalRatings);
      // After submission, the user's own review now exists — mark as editing
      setIsEditingReview(true);
      // Refresh the others list (still filters the user's own review out)
      await loadOthersPage(1, /* append= */ false);
      setOthersPage(1);
    } catch (err) {
      console.error('rateContentWithComment failed:', err);
    } finally {
      setSubmitBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEditingReview ? 'Edit your review' : 'Rate this view'}</DialogTitle>
      <DialogContent>
        {/* Submit / edit section */}
        <Box sx={{ py: 2 }}>
          <Typography variant="body1" gutterBottom>
            How would you rate &quot;{viewName}&quot;?
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
            <Rating
              value={userRating}
              onChange={(_e, v) => setUserRating(v || 0)}
              size="large"
            />
            <Typography variant="body2" color="text.secondary">
              {userRating > 0 ? `${userRating} star${userRating > 1 ? 's' : ''}` : 'Select rating'}
            </Typography>
          </Box>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Review (optional)"
            value={userComment}
            onChange={(e) => setUserComment(e.target.value)}
            placeholder="Share your thoughts about this view..."
          />
        </Box>

        <Divider />

        {/* Browse section */}
        <Box sx={{ pt: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Reviews from your team
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {avgRating > 0 ? `avg ★ ${avgRating.toFixed(1)}` : ''}
              {totalCount > 0 ? ` · ${totalCount}` : ''}
            </Typography>
          </Box>

          {othersLoading && others.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : others.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
              {isEditingReview ? 'No other reviews yet.' : 'Be the first to review this view.'}
            </Typography>
          ) : (
            <Stack
              spacing={2}
              sx={{ maxHeight: 280, overflowY: 'auto', pr: 1 }}
            >
              {others.map((review) => (
                <Box key={review.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Rating value={review.rating} readOnly size="small" />
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {review.userName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {review.createdAt ? review.createdAt.toLocaleDateString() : ''}
                    </Typography>
                  </Box>
                  {review.comment && (
                    <Typography variant="body2" color="text.secondary">
                      {review.comment}
                    </Typography>
                  )}
                </Box>
              ))}
            </Stack>
          )}

          {hasMore && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Button onClick={handleShowMore} disabled={othersLoading} size="small">
                {othersLoading ? 'Loading…' : 'Show more'}
              </Button>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={userRating === 0 || submitBusy}
        >
          {isEditingReview ? 'Update review' : 'Submit review'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RateViewDialog;
